// src/components/forms/variants/corner/types.ts

import type { ReactNode } from 'react'
import { siteConfig } from '~/lib/config/site'
import type { 
  BaseFormProps, 
  ContentConfig, 
  ThemeConfig,
  FeatureListItemConfig,
  PrivacyConfig,
  BadgeConfig,
  SubtitleConfig,
} from '~/components/forms/base/types'

/**
 * Corner-specific theme configuration
 */
export interface CornerThemeConfig extends ThemeConfig {
  /** Whether to collapse to button when inactive */
  collapsible?: boolean
  /** Optional offset configuration */
  offset?: {
    top?: string
    right?: string
    bottom?: string
    left?: string
  }
}

/**
 * Props for the CornerEmailCapture component
 */
export interface CornerEmailCaptureProps extends Omit<BaseFormProps, 'content' | 'theme'> {
  /** Position of the corner form */
  position?: 'bottom-right' | 'bottom-left'
  
  /** Content configuration extending base content */
  content?: ContentConfig & {
    /** Optional floating button configuration */
    floatingButton?: {
      text?: string
      icon?: ReactNode
      className?: string
    }
    /** Optional badge configuration */
    badge?: BadgeConfig
    /** Optional subtitle configuration */
    subtitle?: SubtitleConfig
    /** Optional feature list */
    features?: FeatureListItemConfig[]
    /** Optional privacy notice configuration */
    privacy?: PrivacyConfig
  }
  
  /** Theme configuration extending base theme */
  theme?: CornerThemeConfig
}

/**
 * Default preset configuration
 */
export const CORNER_PRESET: Partial<CornerEmailCaptureProps> = {
  theme: {
    variant: 'corner',
    layout: 'stacked',
    size: 'md',
    theme: 'light',
    collapsible: true,
    offset: {
      bottom: '2rem',
      right: '2rem',
      left: '2rem'
    },
    className: {
      container: 'fixed sm:max-w-sm',
      form: 'rounded-xl bg-white dark:bg-gray-900 shadow-2xl',
      button: 'w-full sm:w-auto'
    }
  },
  content: {
    input: {
      placeholder: 'Enter your email'
    },
    button: {
      text: 'Subscribe',
      loadingText: 'Processing...'
    },
    messages: {
      success: 'Thanks for subscribing!',
      error: 'Please enter a valid email address.'
    },
    privacy: {
      enabled: true,
      text: 'By continuing, you agree to our',
      privacyLink: {
        text: 'Privacy Policy',
        url: `${siteConfig.baseLinks.privacy}`
      },
      termsLink: {
        text: 'Terms of Service',
        url: `${siteConfig.baseLinks.terms}`
      }
    }
  }
} as const 